<template>
  <div
    class="details-container"
    style="overflow: hidden"
    @click.stop="$emit('close')"
  >
    <div class="d-flex flex-column" style="background: #FFFFFF; height: 95vh; width: 780px; margin: auto; overflow: hidden; border-radius: .5em" @click.stop="">
        <header class="flex-shrink-0 py-3">
            <div
                class="col-12 mb-2 pb-2"
                style="border-bottom: #d9d9d9 1px solid"
            >
                <div class="d-flex justify-content-end">
                    <input
                        type="checkbox"
                        class="form-check-input"
                        v-model="showAllPhotos"
                        id="showAllPhotosCheckbox"
                    >
                    <span class="ms-2 me-4">{{ $t('observations.show_all_photos') }}</span>
                    <button class="btn btn-sm btn-secondary mx-1" @click.stop="$emit('close')">{{ $t('common.close') }}</button>
                    <button class="btn btn-sm btn-primary mx-1" :disabled="loading" @click.stop="generatePdf">{{ $t('common.print') }}</button>
                </div>
            </div>
        </header>
        <main ref="mainContent" class="flex-grow-1 overflow-auto p-3" style="overflow-x: hidden !important;">
            <div class="print-page">
                <div class="row">
                    <div class="col-6 nopads">
                        <h1 class="report-header">
                            {{ getTitle() }}
                        </h1>
                        <div>{{ $t('observations.printed_on') }}: {{ getCurrentDay() }}</div>
                        <div v-if="observationDateRangeText">
                            {{ observationDateRangeText }}
                        </div>
                    </div>
                    <div
                        class="col-6 nopads"
                        style="text-align: right"
                    >
                        <img
                            src="../../assets/routa_dark.jpg"
                            class="print-logo"
                            alt="logo"
                        >
                    </div>
                </div>
                <hr>
                <!-- Summary map -->
                <div class="col-12">
                    <div class="map-container summary-map-container">
                        <map-container
                            ref="summaryMapContainer"
                            id="summaryMap"
                            :find-user="false"
                            @onMapChanged="mapChanged"
                        />
                    </div>
                </div>
                <!-- Summary figures -->
                <vue3-datatable
                    v-if="tableItems.length > 0"
                    ref="datatable"
                    :rows="tableItems"
                    :columns="fields"
                    :pagination="false"
                    :sticky-header="true"
                    :sortable="false"
                    :has-checkbox="false"
                    skin="bh-table-compact"
                    row-class="routa-table-row"
                    cell-class="routa-table-cell"
                >
                </vue3-datatable>
            </div>
            <observation-print-details
                v-for="observation in observations"
                ref="observations"
                :key="observation.id"
                :observation="observation"
                :report-completed="reportCompleted"
                :show-all-photos="showAllPhotos"
                @remove="removeItem"
            />
        </main>
        <footer class="flex-shrink-0 p-3 border-top">
            <div class="d-flex justify-content-between">
                <button
                    class="btn btn-sm btn-outline-secondary"
                    @click.stop="scrollToTop"
                >
                    ↑
                </button>

                <button
                    class="btn btn-sm btn-outline-secondary"
                    @click.stop="scrollToBottom"
                >
                    ↓
                </button>
            </div>
        </footer>
    </div>
    <div
      v-if="loading"
      id="loader"
      class="spinner"
    />
  </div>
</template>

<script>
import {timeUtils} from "@/components/mixins/TimeUtils";
import MapContainer from "@/components/map/MapContainer";
import {geomTypes} from "@/components/mixins/ObservationMixin";
import {PdfGeneratorManager} from "@/modules/PdfGeneratorManager";
import {restApi} from "@/components/mixins/RestApiMixin";
import {observationHelper} from '../mixins/ObservationMixin'
import {mapHelper} from '../mixins/MapMixin'
import ObservationPrintDetails from "@/components/observation/ObservationPrintDetails.vue";
import Vue3Datatable from '@bhplugin/vue3-datatable'
import {imageCacheService} from "@/services/ImageCacheService";
import {pdfMixin} from "@/components/mixins/PdfMixin";
import obsSvg from '@/assets/observation.svg'

export default {
    name: 'ObservationDetailsReport',
    components: {ObservationPrintDetails, MapContainer, Vue3Datatable},
    mixins: [timeUtils, restApi, observationHelper, mapHelper, pdfMixin],
    props: {
        reportObservations: {
            type: Array,
            default() {
                return []
            }
        },
        user: {
            type: Object,
            default: null
        },
        companyPreferences: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            map: null,
            icon: null,
            reportCompleted: false,
            showAllPhotos: false,
            loading: false,
            companyLogo: null,
            observations: [],
            GROUP_ID: 0,
            Z_INDEX: {
                BEHIND: 2,
                INFRONT: 5
            },
            fields: [
                {field: 'name', title: this.$t('road_reports.type')},
                {field: 'count', title: this.$t('road_reports.count')}
            ],
            items: [],
            tableItems: [],
        }
    },
    computed: {
        observationDateRangeText() {
            if (!this.observations || this.observations.length === 0) {
                return null
            }

            const times = this.observations
                .map(obs => new Date(obs.created_time).getTime())
                .filter(t => !isNaN(t))

            if (times.length === 0) {
                return null
            }

            const earliest = new Date(Math.min(...times))
            const latest = new Date(Math.max(...times))

            const isSameDay = earliest.toDateString() === latest.toDateString()

            const label = this.$t('observations.report_observation_period')

            if (isSameDay) {
                return label + ': ' + this.getShortDateWithoutTimezone(earliest.toISOString())
            }

            return label + ': ' + this.getShortDateWithoutTimezone(earliest.toISOString()) +
                ' - ' + this.getShortDateWithoutTimezone(latest.toISOString())
        }
    },
    mounted() {
        this.observations = JSON.parse(JSON.stringify(this.reportObservations));
    },
    beforeUnmount() {
        // Allways clear the cache to free memory
        imageCacheService.clear();
    },
    methods: {

        scrollToTop() {
            const container = this.$refs.mainContent;
            if (container) {
                container.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }
        },

        scrollToBottom() {
            const container = this.$refs.mainContent;
            if (container) {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'smooth'
                });
            }
        },

        getTitle() {
            const companyPart = this.user.company && this.user.company.display_name ? this.user.company.display_name : "Routa"

            let title;

            if (this.companyPreferences && this.companyPreferences.observationReportTitle) {
                title = this.companyPreferences.observationReportTitle;
            } else {
                title = this.$t('observations.pdf_title')
            }

            return companyPart + ' - ' + title
        },

        async generatePdf() {
            this.loading = true

            const {
                pdfDoc, pdfService, fonts, colors, footerLogo, companyFooterLogo, pdfGeneratorManager
            } = await this.initializePdf()

            const screenshot = await this.map.takeScreenShot()
            const summaryMap = await new Response(screenshot).arrayBuffer()


            let nextYPosition = await this.addPdfHeaderWithMap(this.getTitle(), summaryMap, this.observationDateRangeText, this.$t('observations.printed_on'))
            const tablePage = pdfDoc.getPage(pdfDoc.getPages().length -1)

            nextYPosition = pdfService.addTable([[this.$t('road_reports.type'), this.$t('road_reports.count')], ...this.items], pdfDoc, {
                textFont: fonts.fontText,
                headerFont: fonts.fontBold,
                textFontSize: 9,
                headerFontSize: 9,
                textColor: colors.textColor,
                headerFontColor: colors.boldColor
            }, nextYPosition, undefined, undefined, footerLogo, undefined, undefined, undefined, companyFooterLogo)

            const afterTablePage = pdfDoc.getPage(pdfDoc.getPages().length -1)

            if (afterTablePage === tablePage) {
                nextYPosition -= 500
            }

            nextYPosition -= 10

            for (let i = 0; i < this.observations.length; i++) {
                const observation = this.observations[i]
                const photos = await this.fetchPhotos(observation)
                nextYPosition = await this.addObservationToPdf(observation, photos, nextYPosition, i)
                // nextYPosition is now updated for the next iteration
            }

            const pdfBytes = await pdfGeneratorManager.generateBytesFromPdfDoc(pdfDoc)
            const fileName = this.companyPreferences?.observationReportTitle ? this.companyPreferences.observationReportTitle : this.$t('observations.pdf_title');
            PdfGeneratorManager.downloadPdf(pdfBytes, this.user && this.user.company && this.user.company.display_name && this.user.company.display_name !== "" ? this.user.company.display_name + " - " + this.$t('common.report') : fileName)
            this.loading = false
        },

        isPng(photo) {
            return photo.startsWith('data:image/png;');
        },

        async takePhotoOfMap(index) {
            const result = this.$refs.observations[index].getMarkerEditorMap()
            const ss = await result.takeScreenShot()
            return await new Response(ss).arrayBuffer();
        },

        async fetchPhotos(observation) {
            const baseUrl = this.observationPhotoUrl
            const id = observation.id
            const results = []

            for (const photo of observation.photos) {
                // now we check the cache first
                if (imageCacheService.has(photo.id.toString())) {
                    results.push(imageCacheService.get(photo.id.toString()));
                } else {
                    let url = baseUrl + '/' + id + '/' + photo.id
                    const response = await new Promise(resolve => this.restFetch(url, resolve))
                    results.push(response.data)
                }
            }
            return results
        },

        mapChanged: function (map) {
            this.map = map
            this.initView()
        },

        initView() {
            // Draw observations on the map
            if (this.map) {
                this.icon = this.map.getMarkerStore().getSvgMarker(obsSvg, null, null, '2em', '2em')
                this.updateObservationsMap()
            }
            // Table summaries
            this.initItems()
        },

        initItems() {
            let table = []
            let pdf = []
            const ids = this.observations.map(ob => ob.type.id)

            const uniqueObservationsByType = this.observations.filter((observation, index, self) =>
                index === self.findIndex((o) => o.type.id === observation.type.id)
            )

            uniqueObservationsByType.forEach(obs => {
                pdf.push([
                        obs.type.name,
                        ids.filter(id => id === obs.type.id).length,
                    ]
                )
                table.push({
                        id: obs.type.id,
                        name: obs.type.name,
                        count: ids.filter(id => id === obs.type.id).length
                    }
                )

            })
            table.sort((a, b) => a.name.localeCompare(b.name));
            this.items = pdf
            this.tableItems = table
        },

        updateObservationsMap() {
            //Remove any previous observations
            this.map.removeMapItemsByType(this.OBSERVATION)
            //Draw the new observations
            this.drawObservations()
            //Zoom to the observations
            this.map.zoomToGroup(this.OBSERVATION)
        },

        observationDetails(obs) {
            const details =  [
                [this.$t('observations.id'), obs.id],
                [this.$t('observations.created_time'), this.toLocalTime(obs.created_time)],
                [this.$t('orders.contract'), obs.contract ? obs.contract.name : ''],
                [this.$t('contracts.customer'), obs.contract ? obs.contract.customer.name : ''],
                [this.$t('observations.order'), obs.work_order && obs.work_order.task_type ? obs.work_order.task_type.name : ''],
            ]

            if (obs.street_name || (obs.road_number && obs.road_number  > 0)) {
                details.push(
                    [this.$t('observations.location'), this.getStreetAddressString(obs) + (obs.city ? ' ' + obs.city.name : '')],
                )
            }

            return details

        },

        drawObservations: function () {
            for (let observation of this.observations) {
                if (observation.type.geometry_type === geomTypes.POINT) {
                    this.map.showMapMarker(observation.id, this.OBSERVATION, observation.geometry.point.y,
                        observation.geometry.point.x, this.icon, observation.closed_time ? this.Z_INDEX.BEHIND : this.Z_INDEX.INFRONT, this.GROUP_ID)
                } else if (observation.type.geometry_type === geomTypes.LINESTRING) {
                    var color = observation.closed_time ? '#bcbcbc' : '#bb330e'
                    var points = observation.geometry.line_string.points.map(point => {
                        return {x: point[0], y: point[1]}
                    })
                    this.map.drawPolyLine(observation.id, this.OBSERVATION, points, color, false, false, 4, 1, true)
                }
            }
        },

        removeItem(observationId) {
            const index = this.observations.findIndex(item => item.id === observationId)
            if (index >= 0) {
                this.observations.splice(index, 1)
                this.initItems()
                this.updateObservationsMap()
            }
        }

    }
}
</script>

<style scoped>
.report-header {
    margin: 0;
    padding-top: .2em;
    font-size: 1.2em;
    font-weight: bold;
    text-transform: uppercase;
}

.summary-map-container {
    position: relative;
    border: 1px solid #d9d9d9;
    border-radius: 1em;
    overflow: hidden;
    width: 750px;
    height: 350px;
    margin: auto;
    margin-bottom: .5em;
}

</style>

